import { Typography, Box, Divider, CircularProgress, Alert } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Dashboard.module.scss';
import { FeatureCardGrid } from '../../components/common/core';
import { FeatureCardProps } from '../../components/common/data/cards/FeatureCard';

/**
 * Pure presentational dashboard view component.
 *
 * This component only handles rendering and receives all data via props.
 * It's separated from the logic-heavy Dashboard wrapper component.
 *
 * @component
 * @category Pages
 * @group Dashboard
 */
interface DashboardViewProps {
  readonly title: string;
  readonly primaryCards: ReadonlyArray<FeatureCardProps>;
  readonly secondaryCards: ReadonlyArray<FeatureCardProps>;
  readonly isLoading?: boolean;
  readonly navigationError?: string | null;
}

export function DashboardView({ title, primaryCards, secondaryCards, isLoading = false, navigationError = null }: DashboardViewProps): ReactElement {
  const { t } = useTranslation(['common']);

  if (isLoading) {
    return (
      <Box className={styles.container}>
        <Typography variant="h5" className={styles.title}>
          {title}
        </Typography>
        <Box className={styles.loadingContainer} data-testid="dashboard-loading">
          <CircularProgress aria-label={t('common:loading')} />
          <Typography variant="body1">{t('common:loading')}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Typography variant="h5" className={styles.title}>
        {title}
      </Typography>

      {navigationError && (
        <Alert severity="error" data-testid="dashboard-navigation-error" sx={{ mb: 2 }}>
          {navigationError}
        </Alert>
      )}

      <Box className={styles.primarySection}>
        <FeatureCardGrid cards={primaryCards} testId="dashboard-primary-grid" />
      </Box>

      <Divider />

      <Box className={styles.secondarySection}>
        <FeatureCardGrid cards={secondaryCards} testId="dashboard-secondary-grid" />
      </Box>
    </Box>
  );
}
